import React, { useState, lazy } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ZapIcon, SmartphoneIcon, TargetIcon, CheckCircleIcon, ArrowRightIcon, GlobeIcon, SearchIcon, LayoutTemplateIcon, MapPinIcon, StarIcon, ChevronDownIcon } from 'lucide-react';
import { ScrollReveal } from '../components/ScrollReveal';
import { SEOHead } from '../components/SEOHead';
import { CodeTypingEffect } from '../components/CodeTypingEffect';
import { AnimatePresence } from 'framer-motion';
const faqs = [{
  question: 'How long does it take to build a new website?',
  answer: 'Most of our Starter and Growth websites are launched within 2-4 weeks. Premium custom builds can take 4-8 weeks depending on the complexity and how quickly we receive your content and feedback.'
}, {
  question: "Do I own the website once it's built?",
  answer: 'Yes, 100%. Unlike some agencies that hold your website hostage on proprietary platforms, we build on standard platforms (like WordPress or React) and hand over full ownership and admin access once the project is paid in full.'
}, {
  question: 'What is Local SEO and why do I need it?',
  answer: 'Local SEO is the process of optimizing your online presence to attract more business from relevant local searches. For home service businesses, this means showing up in the "Google Map Pack" (the top 3 map results) when someone searches for a plumber or roofer in your city.'
}, {
  question: 'Do you write the content for the website?',
  answer: "Yes! We have professional copywriters who specialize in home service industries. We write conversion-focused, SEO-optimized content for all your service pages so you don't have to stare at a blank screen."
}];
const FAQItem = ({
  question,
  answer
}: {
  question: string;
  answer: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return <div className="border-b border-gray-800 last:border-0" data-id="element-2131">
      <button className="w-full py-6 flex items-center justify-between text-left focus:outline-none" onClick={() => setIsOpen(!isOpen)} data-id="element-2132">
        <span className="text-lg font-semibold text-white" data-id="element-2133">{question}</span>
        <motion.div animate={{
        rotate: isOpen ? 180 : 0
      }} transition={{
        duration: 0.2
      }} className="flex-shrink-0 ml-4 text-viridia" data-id="element-2134">
          <ChevronDownIcon className="w-5 h-5" data-id="element-2135" />
        </motion.div>
      </button>
      <AnimatePresence data-id="element-2136">
        {isOpen && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-2137">
            <p className="pb-6 text-gray-400 leading-relaxed" data-id="element-2138">{answer}</p>
          </motion.div>}
      </AnimatePresence>
    </div>;
};
export function WebDesignSeoPage() {
  return <div className="bg-viridia-black min-h-screen" data-id="element-2139">
      <SEOHead title="Web Design & Local SEO | Phoenix Websites That Convert | Viridia Analytics" description="Fast, mobile-first websites with local SEO that turns Phoenix searches into customers. Speed-optimized, conversion-focused web design with Google Business Profile optimization." canonical="/web-design-seo" keywords="web design Phoenix, local SEO Phoenix, mobile-first website, conversion-focused design, Google Business Profile optimization, local search optimization, website development Phoenix" faq={faqs} data-id="element-2140" />
      {/* HERO SECTION */}
      <section className="relative pt-32 pb-20 lg:pt-48 lg:pb-32 overflow-hidden min-h-[90vh] flex items-center" data-id="element-2141">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0 pointer-events-none" data-id="element-2142">
          <div className="absolute top-[10%] left-[10%] w-[40%] h-[40%] rounded-full bg-viridia/10 blur-[120px] animate-pulse-slow" data-id="element-2143"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full" data-id="element-2144">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-2145">
            {/* Hero Content */}
            <div className="max-w-2xl" data-id="element-2146">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6
            }} data-id="element-2147">
                <div className="inline-flex items-center px-3 py-1 rounded-full bg-viridia/10 border border-viridia/20 text-viridia text-sm font-semibold tracking-wide mb-6" data-id="element-2148">
                  WEB DESIGN & SEO
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6" data-id="element-2149">
                  Turn Searches Into{' '}
                  <span className="text-viridia" data-id="element-2150">Customers</span>
                </h1>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-2151">
                  We build fast, conversion-focused websites with local SEO that
                  makes sure your business shows up when Phoenix customers are
                  searching for what you do.
                </p>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-2152">
                  <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-viridia text-white font-semibold hover:bg-viridia-dark transition-colors duration-300" data-id="element-2153">
                    Get a Custom Quote
                  </Link>
                  <Link to="/pricing" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-transparent border border-gray-600 text-white font-semibold hover:bg-white/5 transition-colors duration-300" data-id="element-2154">
                    View Pricing
                  </Link>
                </div>
              </motion.div>
            </div>

            {/* Hero Illustration - Browser Mockup */}
            <div className="relative hidden lg:block" data-id="element-2155">
              <motion.div initial={{
              opacity: 0,
              y: 40,
              rotateX: 10
            }} animate={{
              opacity: 1,
              y: 0,
              rotateX: 0
            }} transition={{
              duration: 0.8,
              delay: 0.2
            }} style={{
              perspective: '1000px'
            }} className="w-full" data-id="element-2156">
                <div className="bg-viridia-surface border border-gray-700 rounded-xl overflow-hidden shadow-2xl transform rotate-y-[-5deg] rotate-z-[2deg]" data-id="element-2157">
                  {/* Browser Chrome */}
                  <div className="bg-gray-900 px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2158">
                    <div className="flex space-x-2" data-id="element-2159">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2160"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2161"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2162"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2163">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2164" />
                      phoenix-local-services.com
                    </div>
                  </div>
                  {/* Real-time Coding Effect */}
                  <CodeTypingEffect data-id="element-2165" />
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE BUILD SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2166">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2167">
          <ScrollReveal data-id="element-2168">
            <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-2169">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2170">
                Websites Built to Convert
              </h2>
              <p className="text-lg text-gray-600" data-id="element-2171">
                Not just pretty — built for speed, mobile-first, and optimized
                to turn visitors into paying customers.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-2172">
            <ScrollReveal delay={0.1} data-id="element-2173">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2174">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2175">
                  <ZapIcon className="w-7 h-7" data-id="element-2176" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2177">
                  Speed-Optimized
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2178">
                  Lightning-fast load times that keep visitors engaged and
                  improve your Google ranking. No bloated templates.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.2} data-id="element-2179">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2180">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2181">
                  <SmartphoneIcon className="w-7 h-7" data-id="element-2182" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2183">
                  Mobile-First Design
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2184">
                  Over 70% of your customers browse on their phone. Every site
                  we build looks and works perfectly on mobile.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={0.3} data-id="element-2185">
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow duration-300 h-full" data-id="element-2186">
                <div className="w-14 h-14 rounded-xl bg-viridia/10 flex items-center justify-center text-viridia mb-6" data-id="element-2187">
                  <TargetIcon className="w-7 h-7" data-id="element-2188" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-2189">
                  Conversion-Focused
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-2190">
                  Strategic layouts, clear CTAs, and trust signals designed to
                  turn visitors into leads and booked jobs.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* LOCAL SEO SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2191">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2192">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-2193">
            <div data-id="element-2194">
              <ScrollReveal data-id="element-2195">
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2196">
                  Local SEO That Gets You Found
                </h2>
                <p className="text-lg text-gray-400 mb-8" data-id="element-2197">
                  When someone in Phoenix searches for your service, you need to
                  show up. We make that happen.
                </p>
                <div className="space-y-4" data-id="element-2198">
                  {['Google Business Profile Optimization', 'Local Keyword Targeting', 'On-Page SEO & Meta Tags', 'Schema Markup for Local Businesses', 'Citation Building & NAP Consistency', 'Monthly SEO Reporting'].map((feature, index) => <div key={index} className="flex items-center" data-id="element-2199">
                      <CheckCircleIcon className="w-5 h-5 text-viridia mr-3 flex-shrink-0" data-id="element-2200" />
                      <span className="text-gray-300" data-id="element-2201">{feature}</span>
                    </div>)}
                </div>
              </ScrollReveal>
            </div>

            <div className="relative" data-id="element-2202">
              <ScrollReveal direction="left" data-id="element-2203">
                {/* Mock Google Search Result */}
                <div className="bg-white rounded-xl p-6 shadow-2xl" data-id="element-2204">
                  <div className="flex items-center gap-4 mb-4 pb-4 border-b border-gray-100" data-id="element-2205">
                    <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center" data-id="element-2206">
                      <MapPinIcon className="w-6 h-6 text-gray-500" data-id="element-2207" />
                    </div>
                    <div data-id="element-2208">
                      <h4 className="text-lg font-bold text-blue-800 hover:underline cursor-pointer" data-id="element-2209">
                        Phoenix Premier Services
                      </h4>
                      <div className="flex items-center text-sm text-gray-600" data-id="element-2210">
                        <span className="text-yellow-500 flex items-center mr-2" data-id="element-2211">
                          4.9 <StarIcon className="w-3 h-3 ml-1 fill-current" data-id="element-2212" />
                        </span>
                        (128) · Service Business
                      </div>
                    </div>
                  </div>
                  <div className="space-y-2" data-id="element-2213">
                    <div className="flex items-center text-sm text-gray-600" data-id="element-2214">
                      <MapPinIcon className="w-4 h-4 mr-2 text-gray-400" data-id="element-2215" />
                      Serving Phoenix, Scottsdale, and surrounding areas
                    </div>
                    <div className="flex items-center text-sm text-green-700 font-medium" data-id="element-2216">
                      <CheckCircleIcon className="w-4 h-4 mr-2" data-id="element-2217" />
                      Open 24/7 · Responds in minutes
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-100 flex gap-2" data-id="element-2218">
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-2219">
                      Website
                    </div>
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-2220">
                      Directions
                    </div>
                    <div className="flex-1 bg-blue-50 text-blue-700 text-center py-2 rounded-full text-sm font-medium cursor-pointer hover:bg-blue-100" data-id="element-2221">
                      Call
                    </div>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </div>
      </section>

      {/* GOOGLE BUSINESS PROFILE SUPPORT */}
      <section className="py-16 bg-viridia-black" data-id="element-2222">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2223">
          <ScrollReveal data-id="element-2224">
            <div className="bg-viridia-surface border border-gray-800 rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-2225">
              <div className="flex items-start gap-4" data-id="element-2226">
                <div className="w-12 h-12 rounded-xl bg-viridia/10 flex items-center justify-center flex-shrink-0" data-id="element-2227">
                  <MapPinIcon className="w-6 h-6 text-viridia" data-id="element-2228" />
                </div>
                <div data-id="element-2229">
                  <h3 className="text-xl font-bold text-white mb-2" data-id="element-2230">
                    Need Help Getting Found on Google Maps?
                  </h3>
                  <p className="text-gray-400 text-sm max-w-xl" data-id="element-2231">
                    If your Google Business Profile isn't set up or verified,
                    you're invisible to local customers. We handle the entire
                    setup and verification process for $397 — so your business
                    is properly listed and ready to go.
                  </p>
                </div>
              </div>
              <Link to="/pricing" className="inline-flex items-center text-viridia hover:text-viridia-dark font-semibold transition-colors whitespace-nowrap" data-id="element-2232">
                View Details <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-2233" />
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* PORTFOLIO PREVIEW SECTION */}
      <section className="py-24 bg-viridia-light" data-id="element-2234">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2235">
          <ScrollReveal data-id="element-2236">
            <div className="text-center mb-16" data-id="element-2237">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-2238">
                Our Work
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-2239">
                Clean, modern websites that represent your brand and drive
                results.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-2240">
            {[{
            name: 'TFP Landscaping',
            type: 'Landscaping & Outdoor Services',
            url: 'https://www.tfplandscaping.com',
            domain: 'tfplandscaping.com',
            image: 'https://cdn.magicpatterns.com/uploads/pNqDjKbbn559dXQNHJrNcf/image.png'
          }, {
            name: 'Finish Coat Paint AZ',
            type: 'Painting & Contracting',
            url: 'https://www.finishcoatpaintaz.com',
            domain: 'finishcoatpaintaz.com',
            image: 'https://cdn.magicpatterns.com/uploads/2MaqmMhJUBbWkMzmHWrQb7/image.png'
          }].map((project, index) => <ScrollReveal key={index} delay={index * 0.2} data-id="element-2241">
                <a href={project.url} target="_blank" rel="noopener noreferrer" className="group block" data-id="element-2242">
                  <div className="bg-gray-900 rounded-t-xl px-4 py-3 flex items-center border-b border-gray-800" data-id="element-2243">
                    <div className="flex space-x-2" data-id="element-2244">
                      <div className="w-3 h-3 rounded-full bg-red-500" data-id="element-2245"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500" data-id="element-2246"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500" data-id="element-2247"></div>
                    </div>
                    <div className="mx-auto bg-gray-800 text-gray-400 text-xs px-4 py-1 rounded-md flex items-center" data-id="element-2248">
                      <SearchIcon className="w-3 h-3 mr-2" data-id="element-2249" />
                      {project.domain}
                    </div>
                  </div>
                  <div className="border-x border-b border-gray-200 rounded-b-xl h-72 relative overflow-hidden group-hover:shadow-xl transition-all duration-300" data-id="element-2250">
                    <img src={project.image} alt={`${project.name} website screenshot`} loading="lazy" className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" data-id="element-2251" />
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-2252">
                      <span className="text-white font-semibold flex items-center" data-id="element-2253">
                        Visit Site <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2254" />
                      </span>
                    </div>
                  </div>
                  <div className="mt-4" data-id="element-2255">
                    <h4 className="text-xl font-bold text-gray-900" data-id="element-2256">
                      {project.name}
                    </h4>
                    <p className="text-gray-500" data-id="element-2257">{project.type}</p>
                  </div>
                </a>
              </ScrollReveal>)}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-24 bg-viridia-black border-t border-gray-800" data-id="element-2258">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2259">
          <ScrollReveal data-id="element-2260">
            <div className="text-center mb-16" data-id="element-2261">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-2262">
                Frequently Asked Questions
              </h2>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.2} data-id="element-2263">
            <div className="bg-viridia-surface rounded-2xl p-6 md:p-8 border border-gray-800" data-id="element-2264">
              {faqs.map((faq, index) => <FAQItem key={index} question={faq.question} answer={faq.answer} data-id="element-2265" />)}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* CTA SECTION */}
      <section className="py-24 bg-gradient-to-br from-viridia-dark to-viridia relative overflow-hidden" data-id="element-2266">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10 mix-blend-overlay" data-id="element-2267"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center" data-id="element-2268">
          <ScrollReveal data-id="element-2269">
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" data-id="element-2270">
              Ready for a Website That Actually Works?
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto" data-id="element-2271">
              Get a custom quote for a site built to convert visitors into
              customers.
            </p>
            <Link to="/contact" className="inline-flex justify-center items-center px-8 py-4 rounded-lg bg-white text-viridia-dark font-bold text-lg hover:bg-gray-100 transition-colors duration-300 shadow-xl hover:shadow-2xl transform hover:-translate-y-1" data-id="element-2272">
              Get Your Custom Quote
              <ArrowRightIcon className="ml-2 w-5 h-5" data-id="element-2273" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>;
}